import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useBlocker } from 'react-router-dom';
import { Dialog } from '../components/dialogs/dialog';
import { Button } from 'primereact/button';

import { Context, Draft, PendingEdit, Ref } from './unsaved-changes-context';

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
    const drafts = useRef(new Set<Ref<Draft>>());
    const edits = useRef(new Set<Ref<PendingEdit>>());
    const bypass = useRef(false);
    const savingForLeave = useRef(false);
    const [action, setAction] = useState<{ run: () => void } | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const pending = useCallback(() => [...edits.current].some(edit => edit.current.dirty()), []);
    const dirty = useCallback((draft: Ref<Draft>) => draft.current.active && (draft.current.dirty() || pending()), [pending]);
    const flushPending = useCallback(() => {
        // Each flush can change callbacks for the remaining fields (e.g. fragment title + HTML).
        [...edits.current].forEach(edit => {
            if (edit.current.dirty()) flushSync(() => edit.current.flush());
        });
    }, []);
    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (bypass.current) return false;
        const blocked = [...drafts.current].some(draft => dirty(draft) && draft.current.leaves(currentLocation, nextLocation));
        // Fragment/tab changes stay inside the draft, but must commit the old input before it unmounts.
        if (!blocked && [...drafts.current].some(draft => draft.current.active)) flushPending();
        return blocked;
    });
    const withoutWarning = useCallback((run: () => void) => {
        const previous = bypass.current;
        bypass.current = true;
        try { run(); } finally { bypass.current = previous; }
    }, []);
    const requestAction = useCallback((run: () => void) => {
        if (!bypass.current && [...drafts.current].some(dirty)) { setError(''); setAction({ run }); }
        else run();
    }, [dirty]);
    // Keep the original blocked destination when creation/renaming normally updates its bookmark.
    const afterSave = useCallback((run: () => void) => {
        if (!savingForLeave.current) withoutWarning(run);
    }, [withoutWarning]);
    const register = useCallback((draft: Ref<Draft>) => {
        drafts.current.add(draft);
        return () => { drafts.current.delete(draft); };
    }, []);
    const registerPending = useCallback((edit: Ref<PendingEdit>) => {
        edits.current.add(edit);
        return () => { edits.current.delete(edit); };
    }, []);
    useEffect(() => {
        const beforeUnload = (event: BeforeUnloadEvent) => {
            if (![...drafts.current].some(dirty)) return;
            event.preventDefault(); event.returnValue = '';
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [dirty]);

    const keepEditing = () => {
        if (busy) return;
        setAction(null); setError('');
        if (blocker.state === 'blocked') blocker.reset();
    };
    const leave = (discard: boolean) => {
        const discarded = discard ? [...drafts.current].filter(dirty) : [];
        [...edits.current].forEach(edit => edit.current.cancel());
        if (discarded.length) withoutWarning(() => flushSync(() => discarded.forEach(draft => draft.current.discard())));
        if (action) {
            withoutWarning(() => {
                action.run();
            });
            setAction(null);
        } else if (blocker.state === 'blocked') blocker.proceed();
        setError('');
    };
    const saveAndLeave = async () => {
        if (busy) return;
        setBusy(true); setError('');
        savingForLeave.current = true;
        try {
            // Commit blur-only inputs and debounced editor HTML before reading save callbacks.
            flushPending();
            for (const draft of [...drafts.current].filter(dirty)) {
                const result = await draft.current.save();
                flushSync(() => {});
                if (result === false || (result !== true && draft.current.active && draft.current.dirty())) {
                    setError('Changes could not be saved. Keep editing to review validation or save errors.');
                    return;
                }
            }
            leave(false);
        } catch {
            setError('Changes could not be saved. Your draft is still open. Please try again.');
        } finally { savingForLeave.current = false; setBusy(false); }
    };
    return <Context.Provider value={{ register, registerPending, requestAction, withoutWarning, afterSave, flushPending }}>
        {children}
        <Dialog header="Unsaved changes" visible={!!action || blocker.state === 'blocked'} modal
            style={{ width: '30rem', maxWidth: '95vw' }} closable={!busy} closeOnEscape={!busy} onHide={keepEditing}
            footer={<div className="admin-toolbar justify-content-end">
                <Button label="Keep editing" outlined disabled={busy} onClick={keepEditing} />
                <Button label="Discard changes" text severity="danger" disabled={busy} onClick={() => leave(true)} />
                <Button label="Save and leave" icon="pi pi-save" loading={busy} disabled={busy} onClick={() => void saveAndLeave()} />
            </div>}>
            <p>You have unsaved changes. Save them before leaving, or discard them?</p>
            {error && <p role="alert">{error}</p>}
        </Dialog>
    </Context.Provider>;
}

